/**
 * Box Truck ZIP Check-ins — when we ask a driver where they are, how often do we find out?
 *
 * A driver answers "where are you?" with five digits, over SMS or in their Telegram group, and we move
 * their pin. Read the RATE, not the count: a company sending hundreds of asks at a 30% rate is not
 * getting locations, it is annoying drivers.
 *
 * The misses column is why this page exists. Until these rows were recorded, only a SUCCESS left a trace:
 * the pin was overwritten and every failure was a log line, so "nobody is checking in" and "everybody is
 * checking in and we are dropping it" looked identical.
 *
 * One case is still NOT here, deliberately: five digits texted from a number that matches no driver on the
 * roster. The SMS surface drops that before the rule is asked, because "60411" from an accountant is not a
 * check-in, and counting it would pad the denominator with messages no driver sent. What IS counted is a
 * number we DO place on a driver and then refuse — reassigned, or not the one we text them on.
 *
 * Backend: GET /v1/gluon/box-truck-zip-analytics (any control-panel operator). Read-only.
 */
import * as React from 'react'
import Alert from '@mui/joy/Alert'
import Box from '@mui/joy/Box'
import Chip from '@mui/joy/Chip'
import CircularProgress from '@mui/joy/CircularProgress'
import LinearProgress from '@mui/joy/LinearProgress'
import Option from '@mui/joy/Option'
import Select from '@mui/joy/Select'
import Sheet from '@mui/joy/Sheet'
import Table from '@mui/joy/Table'
import Tooltip from '@mui/joy/Tooltip'
import Typography from '@mui/joy/Typography'
import PinDropIcon from '@mui/icons-material/PinDrop'
import {
    ANALYTICS_DEFAULT_WINDOW_DAYS,
    ANALYTICS_WINDOW_DAYS,
    ANALYTICS_WINDOW_LABEL,
    type BoxTruckZipAnalytics,
    type BoxTruckZipReportOutcome,
} from '@numeo/types-main-mobile'
import { fetchBoxTruckZipAnalytics } from './api'

/** From the shared set, so the selector offers exactly what the server accepts — a hand-written copy here
 *  is how the other pages came to omit the 180-day window their routers had always taken. */
const WINDOWS = ANALYTICS_WINDOW_DAYS.map((value) => ({ value, label: ANALYTICS_WINDOW_LABEL[value] }))

/** Plain English for an ending, so the column reads without the reader knowing the code. */
const MISS_LABELS: Record<BoxTruckZipReportOutcome, string> = {
    located: 'located',
    // The driver named somewhere they are GOING with no time to go with it: a real code, no fix written,
    // because a fix would claim they are there now. It was missing here, so it rendered as its raw slug.
    not_here_yet: 'heading there, no time given',
    unresolved: 'no such ZIP',
    unavailable: 'lookup down',
    stale: 'too old',
    not_stored: 'write failed',
    superseded: 'newer fix won',
    rate_limited: 'rate-limited',
    not_claimed: 'not their driver',
    claim_unavailable: 'claim unreadable',
    phone_not_textable: 'bad number',
    phone_other_owner: 'number reassigned',
    phone_mismatch: 'unknown number',
    phone_unavailable: 'phone unreadable',
}

/** Where a rate stops being a quirk and starts being a problem worth a message. */
const rateTone = (rate: number): 'success' | 'warning' | 'danger' => (rate >= 80 ? 'success' : rate >= 50 ? 'warning' : 'danger')

const fmtDate = (value: string | null) => (value ? new Date(value).toLocaleString() : '—')

const Stat = ({ label, value, hint }: { label: string; value: string; hint?: string }) => (
    <Sheet variant="soft" sx={{ p: 2, borderRadius: 'md', minWidth: 150, flex: 1 }}>
        <Typography level="body-xs" textColor="text.tertiary">
            {label}
        </Typography>
        <Typography level="h3" sx={{ mt: 0.5 }}>
            {value}
        </Typography>
        {hint && (
            <Typography level="body-xs" textColor="text.tertiary">
                {hint}
            </Typography>
        )}
    </Sheet>
)

export default function BoxTruckZipAnalyticsPage() {
    const [days, setDays] = React.useState<number>(ANALYTICS_DEFAULT_WINDOW_DAYS)
    const [data, setData] = React.useState<BoxTruckZipAnalytics | null>(null)
    const [loading, setLoading] = React.useState(true)
    const [error, setError] = React.useState('')

    React.useEffect(() => {
        let cancelled = false
        setLoading(true)
        setError('')
        // Cleared with the window: keeping the old rows while a new window fails leaves the selector
        // saying "All time" above the 30-day numbers, which reads as the all-time answer.
        setData(null)
        fetchBoxTruckZipAnalytics(days)
            .then((analytics) => {
                if (!cancelled) setData(analytics)
            })
            .catch((err: unknown) => {
                if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load')
            })
            .finally(() => {
                if (!cancelled) setLoading(false)
            })
        return () => {
            cancelled = true
        }
    }, [days])

    const totals = data?.totals
    return (
        <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
                <PinDropIcon />
                <Typography level="h2">Box Truck ZIP Check-ins</Typography>
            </Box>
            <Typography level="body-sm" textColor="text.tertiary" sx={{ mb: 2 }}>
                Every ZIP a driver sent back over SMS or Telegram, and whether it moved their pin. The rate is what to read — a company asking hundreds of times
                at 30% is not getting locations.
            </Typography>

            <Select value={days} onChange={(_, v) => setDays(v ?? ANALYTICS_DEFAULT_WINDOW_DAYS)} size="sm" sx={{ width: 180, mb: 2 }}>
                {WINDOWS.map((w) => (
                    <Option key={w.value} value={w.value}>
                        {w.label}
                    </Option>
                ))}
            </Select>

            {error && (
                <Alert color="danger" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}
            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress />
                </Box>
            )}

            {!loading && data && data.companies.length === 0 && (
                <Alert color="neutral">
                    No ZIP check-ins in this window. A row is written whenever a driver sends five digits back, whatever becomes of it — so an empty page means
                    nobody is being asked, not that the asking is failing.
                </Alert>
            )}

            {!loading && totals && data && data.companies.length > 0 && (
                <>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
                        <Stat
                            label="Check-ins"
                            value={totals.reports.toLocaleString()}
                            hint={`${totals.companies} ${totals.companies === 1 ? 'company' : 'companies'}`}
                        />
                        <Stat label="Located" value={totals.located.toLocaleString()} hint="the pin moved" />
                        <Stat label="Resolve rate" value={`${totals.resolveRate}%`} hint="of all check-ins" />
                        <Stat label="Drivers" value={totals.drivers.toLocaleString()} hint="reported at least once" />
                        <Stat
                            label="By surface"
                            value={`${totals.bySource.sms.toLocaleString()} SMS`}
                            hint={`${totals.bySource.telegram.toLocaleString()} Telegram`}
                        />
                    </Box>

                    {totals.misses.length > 0 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mb: 2 }}>
                            <Typography level="body-xs" textColor="text.tertiary">
                                Across every company, the {totals.reports - totals.located} that missed:
                            </Typography>
                            {/* The whole tail here, not the top three: this is the one place the rarest ending is
                                still visible, and a rare one is often the one worth fixing. */}
                            {totals.misses.map((miss) => (
                                <Chip key={miss.outcome} size="sm" variant="soft" color="neutral">
                                    {MISS_LABELS[miss.outcome] ?? miss.outcome} · {miss.count.toLocaleString()}
                                </Chip>
                            ))}
                        </Box>
                    )}

                    <Sheet variant="outlined" sx={{ borderRadius: 'md', overflow: 'auto' }}>
                        <Table size="sm" stickyHeader hoverRow>
                            <thead>
                                <tr>
                                    <th style={{ width: '22%' }}>Company</th>
                                    <th style={{ width: 110 }}>Check-ins</th>
                                    <th style={{ width: 110 }}>Located</th>
                                    <th style={{ width: 180 }}>Resolve rate</th>
                                    <th style={{ width: 100 }}>Drivers</th>
                                    <th style={{ width: 140 }}>SMS / Telegram</th>
                                    <th>Why the rest missed</th>
                                    <th style={{ width: 170 }}>Last check-in</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.companies.map((company) => (
                                    <tr key={company.applicationId ?? 'unattributed'}>
                                        <td>
                                            {company.projectName ?? (
                                                <Tooltip title="These reports carried no tenant, so they are counted but not attributed.">
                                                    <Typography level="body-sm" textColor="text.tertiary">
                                                        unattributed
                                                    </Typography>
                                                </Tooltip>
                                            )}
                                        </td>
                                        <td>{company.reports.toLocaleString()}</td>
                                        <td>{company.located.toLocaleString()}</td>
                                        <td>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <LinearProgress
                                                    determinate
                                                    value={company.resolveRate}
                                                    color={rateTone(company.resolveRate)}
                                                    sx={{ flex: 1, minWidth: 60 }}
                                                />
                                                <Typography level="body-xs">{company.resolveRate}%</Typography>
                                            </Box>
                                        </td>
                                        <td>{company.drivers.toLocaleString()}</td>
                                        <td>
                                            {company.bySource.sms.toLocaleString()} / {company.bySource.telegram.toLocaleString()}
                                        </td>
                                        <td>
                                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                                {company.misses.length === 0 && (
                                                    <Typography level="body-xs" textColor="text.tertiary">
                                                        —
                                                    </Typography>
                                                )}
                                                {/* The three that explain a low rate. The tail is noise at this width. */}
                                                {company.misses.slice(0, 3).map((miss) => (
                                                    <Chip key={miss.outcome} size="sm" variant="soft" color="neutral">
                                                        {MISS_LABELS[miss.outcome] ?? miss.outcome} · {miss.count}
                                                    </Chip>
                                                ))}
                                            </Box>
                                        </td>
                                        <td>
                                            <Typography level="body-xs">{fmtDate(company.lastReportAt)}</Typography>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </Sheet>
                </>
            )}
        </Box>
    )
}
